"use client";

import { useState, useEffect, useMemo } from "react";
import logo from "../assets/DesarrollandoTerritorios.png"

function RPViewer({
  isOpen = true,
  onClose,
  // ✅ soporta ambos nombres para no romper nada
  rpData,
  formData,
  contractData, // (opcional)
  userRole,     // (opcional)
}) {
  // ✅ toma el que venga
  const incoming = rpData ?? formData ?? null;

  const normalized = useMemo(() => {
    if (!incoming) return null;

    // Caso: { ok: true, data: [...] }
    if (
      typeof incoming === "object" &&
      !Array.isArray(incoming) &&
      Array.isArray(incoming.data)
    ) {
      return incoming.data[0] ?? null;
    }

    // Caso: [... ]
    if (Array.isArray(incoming)) {
      return incoming[0] ?? null;
    }

    // Caso: {...}
    return incoming;
  }, [incoming]);

  const [localForm, setLocalForm] = useState({
    // Cabecera / básicos
    fecha: "",
    dependencia: "",
    objetoIndicador: "",

    // Beneficiario
    nombreBeneficiario: "",
    idBeneficiario: "",

    // Contrato
    numeroContrato: "",
    fechaContrato: "",
    numeroDisponibilidad: "",
    fechaInicioContrato: "",
    fechaFinContrato: "",
    tiempoNecesidadDias: "",

    // Presupuesto / otros
    centroCostos: "",
    componente: "",
    linkSecop: "",

    // Valores
    valorNumerico: "",
    valorLetras: "",

    // Firmas
    solicitante: "",
    firmaUrl: "",
    firmaAlcaldeUrl: "",

    // Rubros
    rubrosData: [],

    // (opcionales si llegan)
    estado_rp: "",
    fechaAprobacion: "",
    numeroRp: "",
    motivoRechazo: "",
  });

  useEffect(() => {
    if (!normalized) return;

    // --- rubrosData: puede venir array, string JSON, o variaciones
    const rawRubros =
      normalized.rubrosData ??
      normalized.rubros_json ??
      normalized.rubros ??
      normalized.rubrosdata ??
      normalized.rubros_data ??
      null;

    let rubrosParsed = [];

    if (Array.isArray(rawRubros)) {
      rubrosParsed = rawRubros;
    } else if (typeof rawRubros === "string" && rawRubros.trim() !== "") {
      try {
        const parsed = JSON.parse(rawRubros);
        rubrosParsed = Array.isArray(parsed) ? parsed : [];
      } catch {
        rubrosParsed = [];
      }
    } else {
      rubrosParsed = [];
    }

    // ✅ RP: tus rubros normalmente tienen resumido, nombreRubro y valor
    rubrosParsed = rubrosParsed.map((r) => ({
      resumido: r?.resumido ?? r?.RESUMIDO ?? r?.codigo ?? r?.CODIGO ?? "",
      nombreRubro:
        r?.nombreRubro ??
        r?.nombre_rubro ??
        r?.NOMBRE_RUBRO ??
        r?.nombre ??
        r?.NOMBRE ??
        "",
      valor: r?.valor ?? r?.VALOR ?? r?.monto ?? r?.MONTO ?? 0,
    }));

    // (opcionales si te llegan desde respuesta/solicitud)
    const estado =
      normalized.estado_rp ||
      normalized.response_status ||
      normalized.status_rp ||
      normalized.estado ||
      "";

    const fechaAprob =
      normalized.fechaAprobacion ||
      normalized.responded_at ||
      normalized.fecha_documento ||
      "";

    const numero =
      normalized.numeroRp ||
      normalized.numero_documento ||
      normalized.numero_rp ||
      "";

    setLocalForm({
      fecha: normalized.fecha || "",
      dependencia: normalized.dependencia || "",
      objetoIndicador: normalized.objetoIndicador || normalized.objeto_indicador || "",

      nombreBeneficiario: normalized.nombreBeneficiario || "",
      idBeneficiario: normalized.idBeneficiario || "",

      numeroContrato: normalized.numeroContrato || "",
      fechaContrato: normalized.fechaContrato || "",
      numeroDisponibilidad: normalized.numeroDisponibilidad || "",

      fechaInicioContrato: normalized.fechaInicioContrato || normalized.fecha_inicio_contrato || "",
      fechaFinContrato: normalized.fechaFinContrato || normalized.fecha_fin_contrato || "",
      tiempoNecesidadDias:
        normalized.tiempoNecesidadDias ??
        normalized.tiempo_necesidad_dias ??
        normalized.tiempoNecesidad ??
        "",

      centroCostos: normalized.centroCostos || normalized.centro_costos || "",
      componente: normalized.componente || "",
      linkSecop: normalized.linkSecop || normalized.link_secop || "",

      valorNumerico: normalized.valorNumerico || normalized.valor_numerico || "",
      valorLetras: normalized.valorLetras || normalized.valor_letras || "",

      solicitante: normalized.solicitante || "",
      firmaUrl: normalized.firmaUrl || normalized.firma_url || "",
      firmaAlcaldeUrl: normalized.firmaAlcaldeUrl || normalized.firma_alcalde_url || "",

      rubrosData: rubrosParsed,

      estado_rp: estado,
      fechaAprobacion: fechaAprob,
      numeroRp: numero,
      motivoRechazo: normalized.motivoRechazo || normalized.motivo_rechazo || "",
    });
  }, [normalized]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("es-CO");
  };

  const rubros = Array.isArray(localForm.rubrosData) ? localForm.rubrosData : [];

  const isValidUrl = (s) => {
    if (!s || typeof s !== "string") return false;
    return /^https?:\/\/\S+/i.test(s.trim());
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        padding: "1rem",
        boxSizing: "border-box",
      }}
    >
      <div
        className="container"
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "white",
          borderRadius: "0.5rem",
        }}
      >
        {/* Header (mismo del CDP, solo cambia el título) */}
        <div className="form-header">
          <div
            style={{
              padding: "5px",
              borderRadius: "4px",
              display: "inline-block",
              backgroundColor: "transparent",
            }}
          >
            <img src={logo || "/placeholder.svg"} alt="Logo" className="logo" />
          </div>

          <div>
            <h3>SOLICITUD DE</h3>
            <h3>REGISTRO PRESUPUESTAL (RP)</h3>
          </div>

          <div className="form-id">
            <span style={{ display: "block" }}>Código: A-CBS-F-013</span>
            <span style={{ display: "block" }}>Versión: 10</span>
            <span style={{ display: "block" }}>Fecha Actualización: 13-01-2025</span>
          </div>
        </div>

        {/* Contenido SOLO LECTURA */}
        <div className="form-content">
          <table className="form-table">
            <tbody>
              <tr>
                <th style={{ width: "200px" }}>FECHA:</th>
                <td>
                  <input
                    type="text"
                    value={formatDate(localForm.fecha)}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>DEPENDENCIA:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.dependencia}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>NOMBRE BENEFICIARIO:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.nombreBeneficiario}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>ID BENEFICIARIO:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.idBeneficiario}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>OBJETO CON INDICADOR DEL PLAN DE DESARROLLO:</th>
                <td>
                  <textarea
                    value={localForm.objetoIndicador}
                    readOnly
                    rows={4}
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>N.º CONTRATO O RESOLUCIÓN:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.numeroContrato}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>FECHA DEL CONTRATO O RESOLUCIÓN:</th>
                <td>
                  <input
                    type="text"
                    value={formatDate(localForm.fechaContrato)}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>N.º DE DISPONIBILIDAD:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.numeroDisponibilidad}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>FECHA INICIO CONTRATO:</th>
                <td>
                  <input
                    type="text"
                    value={formatDate(localForm.fechaInicioContrato)}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>FECHA FIN CONTRATO:</th>
                <td>
                  <input
                    type="text"
                    value={formatDate(localForm.fechaFinContrato)}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>TIEMPO DE LA NECESIDAD (DÍAS):</th>
                <td>
                  <input
                    type="text"
                    value={localForm.tiempoNecesidadDias}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>RUBROS:</th>
                <td>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", border: "1px solid #ccc", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#f5f5f5" }}>
                          <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", minWidth: "120px" }}>
                            RESUMIDO
                          </th>
                          <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", minWidth: "220px" }}>
                            NOMBRE RUBRO
                          </th>
                          <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", minWidth: "120px" }}>
                            VALOR
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rubros.length > 0 ? (
                          rubros.map((rubro, index) => (
                            <tr key={index}>
                              <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                                <input
                                  type="text"
                                  value={rubro.resumido || ""}
                                  readOnly
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    background: "#f3f4f6",
                                    fontSize: "12px",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </td>
                              <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                                <input
                                  type="text"
                                  value={rubro.nombreRubro || ""}
                                  readOnly
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    background: "#f3f4f6",
                                    fontSize: "12px",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </td>
                              <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "right" }}>
                                <input
                                  type="text"
                                  value={Number(rubro.valor || 0).toLocaleString("es-CO")}
                                  readOnly
                                  style={{
                                    width: "100%",
                                    border: "none",
                                    background: "#f3f4f6",
                                    textAlign: "right",
                                    fontSize: "12px",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" style={{ border: "1px solid #ccc", padding: "20px", textAlign: "center", color: "#666" }}>
                              No hay rubros disponibles
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>

              <tr>
                <th>CENTRO DE COSTOS:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.centroCostos}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>COMPONENTE DE INVERSIÓN:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.componente}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              {/* ✅ Aquí el cambio solicitado: mostrar link */}
              <tr>
                <th>LINK SECOP:</th>
                <td>
                  {isValidUrl(localForm.linkSecop) ? (
                    <a href={localForm.linkSecop} target="_blank" rel="noreferrer">
                      {localForm.linkSecop}
                    </a>
                  ) : (
                    <input
                      type="text"
                      value={localForm.linkSecop}
                      readOnly
                      className="form-control"
                      style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                    />
                  )}
                </td>
              </tr>

              <tr>
                <th>VALOR: (En letras)</th>
                <td>
                  <textarea
                    value={localForm.valorLetras}
                    readOnly
                    rows={2}
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              <tr>
                <th>VALOR: (Numérico)</th>
                <td>
                  <input
                    type="text"
                    value={Number(localForm.valorNumerico || 0).toLocaleString("es-CO")}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              {/* Si llegan campos de estado por tu flujo de respuesta, se muestran; si no, no estorban */}
              {!!localForm.estado_rp && (
                <tr>
                  <th>ESTADO RP:</th>
                  <td>
                    <input
                      type="text"
                      value={localForm.estado_rp}
                      readOnly
                      className="form-control"
                      style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                    />
                  </td>
                </tr>
              )}

              {String(localForm.estado_rp || "").toLowerCase() === "aprobado" && (
                <>
                  <tr>
                    <th>FECHA DE APROBACIÓN:</th>
                    <td>
                      <input
                        type="text"
                        value={formatDate(localForm.fechaAprobacion || "")}
                        readOnly
                        className="form-control"
                        style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>NÚMERO RP:</th>
                    <td>
                      <input
                        type="text"
                        value={localForm.numeroRp || ""}
                        readOnly
                        className="form-control"
                        style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                      />
                    </td>
                  </tr>
                </>
              )}

              {String(localForm.estado_rp || "").toLowerCase() === "negado" && (
                <tr>
                  <th>MOTIVO RECHAZO:</th>
                  <td>
                    <textarea
                      value={localForm.motivoRechazo || ""}
                      readOnly
                      rows={3}
                      className="form-control"
                      style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                    />
                  </td>
                </tr>
              )}

              <tr>
                <th>SOLICITANTE:</th>
                <td>
                  <input
                    type="text"
                    value={localForm.solicitante}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                  />
                </td>
              </tr>

              {localForm.firmaUrl && (
                <tr>
                  <th>FIRMA SOLICITANTE:</th>
                  <td>
                    <img
                      src={localForm.firmaUrl || "/placeholder.svg"}
                      alt="Firma del solicitante"
                      style={{ maxWidth: "200px", maxHeight: "100px" }}
                    />
                  </td>
                </tr>
              )}

              {localForm.firmaAlcaldeUrl && (
                <tr>
                  <th>FIRMA ALCALDE:</th>
                  <td>
                    <img
                      src={localForm.firmaAlcaldeUrl}
                      alt="Firma del alcalde"
                      style={{ maxWidth: "200px", maxHeight: "100px" }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          className="form-actions"
          style={{ display: "flex", justifyContent: "flex-end", padding: "1rem", gap: "0.5rem" }}
        >
          <button onClick={onClose} className="btn btn-secondary">
            Cerrar
          </button>
        </div>

        <div className="form-footer">
          <p>Página 1 de 1 • Oficina Jurídica • SOLO LECTURA</p>
        </div>
      </div>
    </div>
  );
}

export default RPViewer;
